import { create } from 'zustand';
import {
  ontologyApi,
  type OntologyGraph,
  type OntologyVersionInfo,
  type OntologyDiff,
  type OntologyNodeData,
  type OntologyClientsResponse,
} from '@/services/api';

export type OntologyTab = 'explorer' | 'evolution' | 'reference';

export type EntityTypeFilter = 'ATTRIBUTE' | 'NEED' | 'VALUE' | 'TRUST' | 'PRODUCT' | 'OTHER' | 'MARKET_CONCEPT' | string;

export interface OntologyFilters {
  entityTypes: EntityTypeFilter[];
  categories: string[];
  relTypes: string[];
  searchQuery: string;
}

interface OntologyState {
  // Config
  selectedClient: string;
  availableClients: string[];
  selectedVersion: string | null;
  activeTab: OntologyTab;

  // Data
  versions: OntologyVersionInfo[];
  graphByVersion: Record<string, OntologyGraph>;
  diffByTransition: Record<string, OntologyDiff>; // key: "v8__v8.1"
  referenceDoc: { name: string; content: string } | null;

  // Interaction
  focusedNodeId: string | null;
  filters: OntologyFilters;

  // Evolution playback
  isPlaying: boolean;
  playVersionIndex: number;

  // UI state
  fullscreen: boolean;

  // Loading
  versionsLoading: boolean;
  graphLoading: boolean;
  diffLoading: boolean;
  referenceLoading: boolean;

  // Errors
  versionsError: string | null;
  graphError: string | null;

  // Actions
  setClient: (clientUid: string) => void;
  setVersion: (version: string) => void;
  setTab: (tab: OntologyTab) => void;
  setFocusedNode: (nodeId: string | null) => void;
  setFilters: (filters: Partial<OntologyFilters>) => void;
  setIsPlaying: (playing: boolean) => void;
  setPlayVersionIndex: (idx: number) => void;
  setFullscreen: (fs: boolean) => void;

  fetchClients: () => Promise<void>;
  fetchVersions: (clientUid?: string) => Promise<void>;
  fetchGraph: (version?: string) => Promise<void>;
  fetchDiff: (fromVersion: string, toVersion: string) => Promise<OntologyDiff | null>;
  fetchReference: (docName: string) => Promise<void>;

  // Derived helpers
  currentGraph: () => OntologyGraph | null;
  filteredGraph: () => OntologyGraph | null;
  getDiff: (from: string, to: string) => OntologyDiff | null;
  getNode: (nodeId: string) => OntologyNodeData | null;
}

const DEFAULT_FILTERS: OntologyFilters = {
  entityTypes: [],
  categories: [],
  relTypes: [],
  searchQuery: '',
};

export const useOntologyStore = create<OntologyState>((set, get) => ({
  selectedClient: 'skoutorganic',
  availableClients: ['skoutorganic'],
  selectedVersion: null,
  activeTab: 'explorer',

  versions: [],
  graphByVersion: {},
  diffByTransition: {},
  referenceDoc: null,

  focusedNodeId: null,
  filters: DEFAULT_FILTERS,

  isPlaying: false,
  playVersionIndex: 0,

  fullscreen: false,

  versionsLoading: false,
  graphLoading: false,
  diffLoading: false,
  referenceLoading: false,

  versionsError: null,
  graphError: null,

  setClient: (clientUid) => {
    set({ selectedClient: clientUid, selectedVersion: null, versions: [], graphByVersion: {}, diffByTransition: {} });
    get().fetchVersions(clientUid);
  },

  setVersion: (version) => {
    set({ selectedVersion: version });
    get().fetchGraph(version);
  },

  setTab: (tab) => set({ activeTab: tab }),

  setFocusedNode: (nodeId) => set({ focusedNodeId: nodeId }),

  setFilters: (partial) =>
    set((s) => ({ filters: { ...s.filters, ...partial } })),

  setIsPlaying: (playing) => set({ isPlaying: playing }),
  setPlayVersionIndex: (idx) => set({ playVersionIndex: idx }),
  setFullscreen: (fs) => set({ fullscreen: fs }),

  fetchClients: async () => {
    try {
      const res = await ontologyApi.listClients();
      const clients = res.clients ?? [];
      const defaultClient = res.default ?? clients[0] ?? 'skoutorganic';
      set({ availableClients: clients.length > 0 ? clients : ['skoutorganic'] });
      // Only update selectedClient if the current one is the bootstrap default and
      // the API returns a better default (i.e. avoid overriding user selection).
      if (get().selectedClient === 'skoutorganic' && defaultClient !== 'skoutorganic') {
        get().setClient(defaultClient);
      } else if (!clients.includes(get().selectedClient) && clients.length > 0) {
        get().setClient(defaultClient);
      }
    } catch {
      // Non-fatal; keep bootstrap default
    }
  },

  fetchVersions: async (clientUid) => {
    const client = clientUid ?? get().selectedClient;
    set({ versionsLoading: true, versionsError: null });
    try {
      const res = await ontologyApi.listVersions(client);
      const versions = res.versions ?? [];
      set({
        versions,
        versionsLoading: false,
        selectedVersion: versions.length > 0 ? versions[versions.length - 1].version : null,
      });
      // Auto-load the latest version graph
      const latest = versions[versions.length - 1];
      if (latest) {
        get().fetchGraph(latest.version);
      }
    } catch (e) {
      set({ versionsLoading: false, versionsError: (e as Error).message });
    }
  },

  fetchGraph: async (version) => {
    const v = version ?? get().selectedVersion;
    if (!v) return;
    const { graphByVersion, selectedClient } = get();
    if (graphByVersion[v]) return; // already cached
    set({ graphLoading: true, graphError: null });
    try {
      const graph = await ontologyApi.getGraph(selectedClient, v);
      set((s) => ({
        graphByVersion: { ...s.graphByVersion, [v]: graph },
        graphLoading: false,
      }));
    } catch (e) {
      set({ graphLoading: false, graphError: (e as Error).message });
    }
  },

  fetchDiff: async (fromVersion, toVersion) => {
    const key = `${fromVersion}__${toVersion}`;
    const existing = get().diffByTransition[key];
    if (existing) return existing;
    set({ diffLoading: true });
    try {
      const diff = await ontologyApi.getDiff(get().selectedClient, fromVersion, toVersion);
      set((s) => ({
        diffByTransition: { ...s.diffByTransition, [key]: diff },
        diffLoading: false,
      }));
      return diff;
    } catch {
      set({ diffLoading: false });
      return null;
    }
  },

  fetchReference: async (docName) => {
    const current = get().referenceDoc;
    if (current?.name === docName) return;
    set({ referenceLoading: true });
    try {
      const res = await ontologyApi.getReference(docName);
      set({ referenceDoc: { name: docName, content: res.content }, referenceLoading: false });
    } catch {
      set({ referenceLoading: false });
    }
  },

  currentGraph: () => {
    const { selectedVersion, graphByVersion } = get();
    return selectedVersion ? (graphByVersion[selectedVersion] ?? null) : null;
  },

  filteredGraph: () => {
    const graph = get().currentGraph();
    if (!graph) return null;
    const { entityTypes, categories, relTypes, searchQuery } = get().filters;
    const q = searchQuery.toLowerCase();

    const filteredNodes = graph.nodes.filter((n) => {
      if (entityTypes.length > 0 && !entityTypes.includes(n.entity_type as EntityTypeFilter)) return false;
      if (categories.length > 0 && !categories.includes(n.category)) return false;
      if (q && !n.name.toLowerCase().includes(q) && !n.id.toLowerCase().includes(q)) return false;
      return true;
    });
    const nodeSet = new Set(filteredNodes.map((n) => n.id));
    const filteredEdges = graph.edges.filter((e) => {
      if (!nodeSet.has(e.source) || !nodeSet.has(e.target)) return false;
      if (relTypes.length > 0 && !relTypes.includes(e.rel_type)) return false;
      return true;
    });
    return { ...graph, nodes: filteredNodes, edges: filteredEdges };
  },

  getDiff: (from, to) => {
    return get().diffByTransition[`${from}__${to}`] ?? null;
  },

  getNode: (nodeId) => {
    const graph = get().currentGraph();
    return graph?.nodes.find((n) => n.id === nodeId) ?? null;
  },
}));
